import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * `OfficeIntervalManager` is a singleton that manages a set of callbacks.
 * It executes all attached callbacks synchronously ensuring that all `OfficeAvailability`
 * instances update simultaneously.
 */
class OfficeIntervalManager {
  constructor() {
    if (OfficeIntervalManager.instance) {
      return OfficeIntervalManager.instance
    }
    /**
     * @type Array<()=>void>
     */
    this.callbacks = []
    this.interval = setInterval(() => {
      this.callbacks.forEach(callback => {
        try {
          callback()
        } catch (error) {
          console.error(error)
        }
      })
    }, 60_000)
    OfficeIntervalManager.instance = this
  }

  attachCallback(callback) {
    this.callbacks.push(callback)
  }
}

class OfficeAvailability extends HTMLElement {
  constructor() {
    super()
    try {
      this.timezone = this.getAttribute('data-timezone')
      if (!this.timezone) {
        throw new Error(
          `Cannot initialize "OfficeAvailability". Expected "data-timezone to have valid IANA timezone, but "${this.timezone}" was provided instead`
        )
      }
      this.openTime = this.parseTime(this.getAttribute('data-open-time'))
      this.closeTime = this.parseTime(this.getAttribute('data-close-time'))
      this.secondaryColors = this.dataset.secondaryColors || false
    } catch (error) {
      console.error(
        "An error has occurred while initializing 'OfficeAvailability'. This is likely because of a missing data- attribute.",
        error
      )
      /**
       * Hide the element since it's not functional
       */
      this.style.display = 'none'
    }
    this.attachShadow({ mode: 'open' })
  }

  /**
   * @param {string} time string in "am/pm" format
   * @returns {number} `time` in 24h format. type `number`
   */
  parseTime(time) {
    if (typeof time !== 'string')
      throw new Error(
        `"OfficeAvailability.parseTime" expects "time" to be a string, but received "${time}" (typeof ${time}) instead`
      )
    return Number(dayjs(`1/1/1 ${time}`).format('H'))
  }

  connectedCallback() {
    const slot = document.createElement('slot')
    slot.setAttribute('name', 'content')
    this.shadowRoot.append(slot)
    this.updateOfficeAvailability(slot)

    new OfficeIntervalManager().attachCallback(() => {
      this.updateOfficeAvailability(slot)
    })
  }

  /**
   * Determines if the office is available by comparing the current hour in the office's
   * timezone against the configured open and close times.
   *
   * @returns {boolean} - True if the current time in the specified timezone is within office hours, false otherwise.
   */
  isOfficeAvailable() {
    const currentTime = dayjs().tz(this.timezone)
    const currentHour = currentTime.hour()
    return currentHour >= this.openTime && currentHour < this.closeTime
  }

  /**
   * Updates the display elements within the provided slot container to reflect the current
   * office availability status and the current local time in the office's timezone.
   *
   * @param {HTMLSlotElement} slot - The slot container where the display elements are located.
   */
  updateOfficeAvailability(slot) {
    // Get the content assigned to the slot
    const content = slot.assignedNodes()?.[0]

    if (!content) {
      console.error('OfficeAvailability content slot not provided.')
      return
    }

    const currentAvailabilityDisplay = content.querySelector('[data-current-availability]')
    const currentAvailabilityDisplayIcon = content.querySelector('[data-current-availability-icon]')
    const currentTimeDisplay = content.querySelector('[data-current-time]')

    const officeAvailable = this.isOfficeAvailable()
    const officeTime = dayjs().tz(this.timezone).format('hh:mm a')

    try {
      currentAvailabilityDisplay.innerHTML = officeAvailable ? 'open' : 'closed'
      currentTimeDisplay.innerHTML = officeTime

      if (officeAvailable) {
        if (!this.secondaryColors) {
          currentAvailabilityDisplayIcon.classList.add('bg-b2b')
          currentAvailabilityDisplayIcon.classList.remove('bg-health')
        } else {
          currentAvailabilityDisplayIcon.classList.add('bg-b2b')
          currentAvailabilityDisplayIcon.classList.remove('bg-health')
        }
      } else {
        if (!this.secondaryColors) {
          currentAvailabilityDisplayIcon.classList.add('bg-health')
          currentAvailabilityDisplayIcon.classList.remove('bg-b2b')
        } else {
          currentAvailabilityDisplayIcon.classList.add('bg-health')
          currentAvailabilityDisplayIcon.classList.remove('bg-b2b')
        }
      }
    } catch (error) {
      console.error(
        'An error has occurred while trying to update a child of an OfficeAvailability instance. Check all required children are present in the DOM.',
        error
      )
    }
  }
}

if (!customElements.get('office-availability')) {
  customElements.define('office-availability', OfficeAvailability)
}
